/**
 * AEGIS — the co-pilot screen, and everything around it.
 *
 * Two shells, chosen by route. Public routes (sign in, sign up, plans, the two
 * Stripe returns) render on their own, because a login form inside the trading
 * chrome is a login form that has to load market data to draw itself. Everything
 * else renders inside the app shell: rail, header, content.
 *
 * No component in this tree contains a hex colour, a pixel value, or a font
 * name. Everything resolves through `@aegis/ui`, so the redesign pass
 * can replace that file rather than the application. See docs/18.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { ScenariosResponse, ScoreResponse } from '@aegis/api-client';
import type { Pane, PanelKind } from '@aegis/core';
import { attachPane, crosshairTargets, findPane, panesIn, primaryChartPane } from '@aegis/core';
import { ChartPane, OwnChartPane } from './components/ChartPane.js';
import { ImportScreen } from './screens/Import.js';
import {
  IDLE_REPLAY, ReplayControls, type ReplayState,
} from './components/ReplayControls.js';
import { OrderBook } from './components/OrderBook.js';
import { Footprint } from './components/Footprint.js';
import { ProfilePanel } from './components/ProfilePanel.js';
import { StudiesPanel } from './components/StudiesPanel.js';
import type { ReplayContext } from './lib/replaySeries.js';
import { Tape } from './components/Tape.js';
import { NotesPanel } from './components/NotesPanel.js';
import { PrecedentPanel } from './components/Precedent.js';
import { PortfolioRiskPanel } from './components/PortfolioRisk.js';
import { WorkspaceBar } from './components/WorkspaceBar.js';
import { WorkspaceGrid } from './components/WorkspaceGrid.js';
import { paneSymbolOf, useWorkspaces } from './lib/workspace.js';
import { createCrosshairBus } from './lib/crosshair.js';
import { closePane, detachableKinds, openPane, openPaneKinds } from './lib/panes.js';
import { SymbolNews } from './components/SymbolNews.js';
import { SymbolPicker } from './components/SymbolPicker.js';
import { loadLastSymbol, saveLastSymbol } from './lib/lastSymbol.js';
import { JournalEditor } from './components/JournalEditor.js';
import { AnalysisPanel, AnalyticsPanel, RulePanel, StatusDot, TradeTable, Pnl } from './components/panels.js';
import { buildCatalog, defaultSymbol } from '@aegis/core';
import { DEFAULT_CHART_STYLE, isChartStyle, type ChartStyle } from '@aegis/ui';
import { delayLabel, formatNetChange, netChange } from '@aegis/api-client';
import {
  api, ApiError, fmtUsd,
  type Account, type Analytics, type Health,
  type Instrument, type RuleState, type Trade,
} from './lib/api.js';
import { fmtNum } from './lib/api.js';

import { Icon } from './components/Icon.js';
import { EmptyState } from './components/EmptyState.js';
import { Link, navigate, useRoute } from './lib/router.js';
import { CreditMeter } from './components/CreditMeter.js';
import { hasIdentity, useSession } from './lib/session.js';
import {
  loadPinned, savePinned, modifierKey, shortcutIndex, PEEK_DELAY_MS,
} from './lib/rail.js';
import { zoneResolver } from './lib/zone.js';
import type { Instrument as ApiInstrument } from '@aegis/api-client';
import { useMarketFeed } from './lib/marketFeed.js';
import { useChartData } from './lib/chartData.js';
import { useDrawings } from './lib/drawings.js';
import { AuthScreen } from './screens/Auth.js';
import { FinishSetupNudge, OPEN_ONBOARDING, OnboardingFlow } from './screens/Onboarding.js';
import { CheckoutCancelledScreen, CheckoutSuccessScreen } from './screens/BillingReturn.js';
import { AlertsScreen } from './screens/Alerts.js';
import { FundamentalsScreen } from './screens/Fundamentals.js';
import { BrokerScreen } from './screens/Broker.js';
import { DevicesScreen } from './screens/Devices.js';
import { PricingScreen } from './screens/Pricing.js';
import { StrategiesScreen } from './screens/Strategies.js';
import { TradeDetailScreen } from './screens/TradeDetail.js';
import { DailyScreen } from './screens/Daily.js';
import { ReportsScreen } from './screens/Reports.js';
import { AgentsScreen } from './screens/Agents.js';
import { BacktestScreen } from './screens/Backtest.js';
import { ScenarioPanel, ScorePanel } from './components/ScorePanel.js';
import {
  ForgotPasswordScreen, ResetPasswordScreen, VerifyEmailScreen,
} from './screens/Recovery.js';
import { SecurityScreen } from './screens/Security.js';
import { PreferencesScreen } from './screens/Preferences.js';
import { SettingsScreen } from './screens/Settings.js';
import { ErrorsScreen } from './screens/Errors.js';
import { NewsStatusScreen } from './screens/admin/NewsStatus.js';
import { AiStatusScreen } from './screens/admin/AiStatus.js';
import { AiAuditScreen } from './screens/admin/AiAudit.js';
import { EntitlementsScreen } from './screens/admin/Entitlements.js';
import { TaxScreen } from './screens/Tax.js';
import { UpdateNotice } from './components/UpdateNotice.js';
import { titleFor } from './lib/title.js';
import { useIsMobile } from './lib/viewport.js';
import { MobileTabBar } from './components/MobileTabBar.js';
import { MoreScreen } from './screens/More.js';

const TIMEFRAMES = ['1m', '5m', '15m', '1h', '1d'] as const;

/** Routes that render without the trading shell and without a session. */
const PUBLIC_ROUTES = new Set([
  '/login', '/signup', '/pricing', '/billing/success', '/billing/cancelled',
  // Recovery is public by necessity: someone who cannot sign in is exactly who
  // needs these, and `/verify-email` has to work from a mail client that carries
  // no session.
  '/forgot-password', '/reset-password', '/verify-email',
]);

export function App(): JSX.Element {
  const route = useRoute();
  const { status } = useSession();

  // The tab is how you find this window again when it is one of twelve.
  useEffect(() => {
    document.title = titleFor(route.path);
  }, [route.path]);

  if (PUBLIC_ROUTES.has(route.path)) {
    switch (route.path) {
      case '/login': return <AuthScreen mode="login" />;
      case '/signup': return <AuthScreen mode="signup" />;
      case '/pricing': return <PricingScreen />;
      case '/billing/success': return <CheckoutSuccessScreen />;
      case '/billing/cancelled': return <CheckoutCancelledScreen />;
      case '/forgot-password': return <ForgotPasswordScreen />;
      case '/reset-password': return <ResetPasswordScreen />;
      default: return <VerifyEmailScreen />;
    }
  }

  // The first `/v1/auth/me` has not answered yet. Rendering the app now would
  // fire every data request as an anonymous user and then fire them all again.
  if (status === 'loading') {
    return (
      <div className="boot">
        <span className="brand">AEGIS</span>
        <span className="dim">Starting…</span>
      </div>
    );
  }

  // No session and no demo identity: the login wall is the correct answer here,
  // and only here. In mock mode `status` is `demo` and this never fires — which
  // is what keeps `pnpm dev` on a fresh clone from opening on a login form with
  // no account to log into.
  if (!hasIdentity(status)) {
    return <AuthScreen mode="login" />;
  }

  return (
    <>
      <AppShell route={route.path} />
      {/*
        Over the app, not in front of it.

        A signup funnel withholds the product pending answers; this does not.
        The app is rendered and reachable behind the card, `Skip for now` is on
        every screen, and Escape skips — which is the difference between asking
        and gating.
      */}
      <FirstRun />
    </>
  );
}

/**
 * The first-run questionnaire, if it is due.
 *
 * The server decides whether it is, so the phone and the desktop cannot
 * disagree and a demo session never opens a wizard for an account with nothing
 * to configure. A failure to ask is a failure to show it: an onboarding flow is
 * never worth blocking the product over.
 */
function FirstRun(): JSX.Element | null {
  const [due, setDue] = useState(false);

  useEffect(() => {
    let live = true;
    void api.onboarding()
      .then((s) => { if (live) setDue(s.due); })
      .catch(() => { /* Never a reason to interrupt somebody. */ });
    return () => { live = false; };
  }, []);

  // Settings and the skip nudge re-open it. Answers are already stored, so
  // resuming picks up mid-flow rather than starting again.
  useEffect(() => {
    const open = () => setDue(true);
    window.addEventListener(OPEN_ONBOARDING, open);
    return () => window.removeEventListener(OPEN_ONBOARDING, open);
  }, []);

  if (!due) return null;
  // Reloading rather than clearing local state: finishing writes a watchlist,
  // preferences and a rule set, and every screen already mounted is holding
  // the values from before that.
  return <OnboardingFlow onDone={() => window.location.reload()} />;
}

/**
 * What a brand-new account sees where its trades would be.
 *
 * Accounts are per user, so a fresh signup genuinely has none until a broker is
 * connected. The empty state says which broker adapter the deployment is
 * running and what it would take to fill this in, rather than leaving the user
 * to wonder whether the product is broken.
 */
function NoBrokerYet({ health }: { health: Health | null }): JSX.Element {
  const mock = health?.broker === 'mock' || health?.mode === 'mock';

  return (
    <EmptyState
      icon="broker"
      title="No broker connected"
      action={<Link to="/settings/broker" className="btn btn-primary compact">Connect a broker</Link>}
      note={
        <>
          {mock ? (
            <p>
              This deployment runs the mock adapter, which only seeds the demo
              identity. A real account needs a broker adapter configured on the
              server — <code className="mono">BROKER_ADAPTER=rithmic</code> and
              credentials.
            </p>
          ) : (
            <p>
              The <code className="mono">{health?.broker ?? 'broker'}</code> adapter is
              configured here: {health?.brokerHealth.reason ?? 'status unknown'}.
            </p>
          )}
          <p>
            The chart, the annotations and the analysis do not wait for this — they
            work with no broker attached at all.
          </p>
        </>
      }
    >
      <p>
        Your journal fills itself from your broker&rsquo;s fills — there is nothing to
        type in. Until an account is connected there is nothing to reconstruct.
      </p>
    </EmptyState>
  );
}

function AppShell({ route }: { route: string }): JSX.Element {
  const { status, user, notice, dismissNotice } = useSession();

  const [health, setHealth] = useState<Health | null>(null);
  const [symbols, setSymbols] = useState<string[]>([]);
  /**
   * The instrument master, for tick size and price precision.
   *
   * The chart used to format prices with a hard-coded `minMove: 0.25` —
   * E-mini S&P's tick. Fourteen of the eighteen listed contracts have a
   * different one, so crude oil's axis snapped to quarter-dollars against a
   * real tick of a cent, and the Dow showed decimal places it does not have.
   * CLAUDE.md is explicit that tick size comes from here and is never
   * hard-coded.
   */
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  /**
   * Starts empty, not at `ES`.
   *
   * A literal here is a house trading style, which CLAUDE.md rules out, and it
   * was also wrong on its own terms: the chart rendered ES before the feed had
   * confirmed it carries ES. The real value is resolved in the boot effect from
   * `last viewed -> first pinned -> first listed`. See `defaultSymbol`.
   */
  const [symbol, setSymbol] = useState('');
  const [watchlist, setWatchlist] = useState<string[]>([]);
  const [watchlistMax, setWatchlistMax] = useState(12);
  const [timeframe, setTimeframe] = useState<string>('5m');

  const [showAnnotations, setShowAnnotations] = useState(true);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [openTrades, setOpenTrades] = useState<Trade[]>([]);
  const [rules, setRules] = useState<RuleState | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [overlay, setOverlay] = useState<{ tradeCount: number; winRate: string | null; netPnl: string; note: string } | null>(null);
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);

  /**
   * The session timezone, for every table of timestamps.
   *
   * Loaded once and held, because it changes only when the user changes it and
   * a per-table fetch would put two tables in different zones for one paint.
   * Null until it arrives — a header that names the wrong zone is worse than a
   * header that names none, so the tables show no zone until this resolves.
   */
  const [tzPref, setTzPref] = useState<string | null>(null);
  const [zoneInstruments, setZoneInstruments] = useState<readonly ApiInstrument[]>([]);
  /** How the trader wants the series drawn. Candles until their answer lands. */
  const [chartStyle, setChartStyle] = useState<ChartStyle>(DEFAULT_CHART_STYLE);
  /**
   * Whether the chart has already been opened on the trader's own timeframe.
   *
   * The set-up questionnaire promises "where the chart opens, every time", so
   * the preference seeds the timeframe once per load and then gets out of the
   * way — a preference that reasserted itself would undo every pill the trader
   * pressed afterwards.
   *
   * A ref rather than state because nothing renders from it, and a re-render
   * between the fetch resolving and the flag being set is exactly the window
   * in which it would apply twice.
   */
  const seededTimeframe = useRef(false);
  useEffect(() => {
    if (!hasIdentity(status)) { setTzPref(null); return; }
    let live = true;
    void Promise.all([api.preferences(), api.instruments()])
      .then(([p, i]) => {
        if (!live) return;
        setTzPref(p.timezone);
        setZoneInstruments(i.instruments);
        // Tolerant of a value this build does not know: a preference outlives
        // the code that wrote it, and an unreadable one must not blank the
        // chart. See `chartStyleShape`.
        setChartStyle(isChartStyle(p.chartStyle) ? p.chartStyle : DEFAULT_CHART_STYLE);
        /*
          `4h` is in the preference's own enum and is NOT in this list — the web
          chart offers five timeframes and the schema allows six. A preference
          this surface cannot draw is left unapplied rather than approximated,
          so the chart opens on 5m and the pill row still matches what is drawn.
        */
        if (!seededTimeframe.current
            && (TIMEFRAMES as readonly string[]).includes(p.defaultTimeframe)) {
          seededTimeframe.current = true;
          setTimeframe(p.defaultTimeframe);
        }
      })
      // A failed preference load must not blank the journal. The tables fall
      // back to naming no zone, which is honest rather than wrong.
      .catch(() => { if (live) setTzPref(null); });
    return () => { live = false; };
  }, [status]);
  const [syncing, setSyncing] = useState(false);

  const [error, setError] = useState<string | null>(null);

  /**
   * Everything the series is, for the symbol and timeframe on screen.
   *
   * This was two dozen `useState` calls and three effects in this component,
   * which is why the app could hold exactly one chart. It is a hook now so a
   * pane can call it too — see `lib/chartData.ts`.
   */
  const chartData = useChartData({ symbol, timeframe, onError: setError });
  const {
    bars, barSeconds, daily, lastPrice, delayedByMs, dataSource, dataReason,
    marketStatus, loading: chartLoading, analysis, disclaimer, setAnalysis,
  } = chartData;

  // --- drawings -------------------------------------------------------------
  const [score, setScore] = useState<ScoreResponse | null>(null);
  const [scenarios, setScenarios] = useState<ScenariosResponse | null>(null);
  /**
   * The drawings on this screen's chart, and the tool being used on it.
   *
   * A hook so a second chart pane can own its own — the tool and the selection
   * are per-chart, or drawing a trendline on one pane arms every other.
   */
  const drawingState = useDrawings(symbol, { onError: setError });

  // --- on-demand analysis ---------------------------------------------------
  const [quota, setQuota] = useState<{ limit: number; used: number } | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void api.analysisQuota()
      .then((q) => setQuota({ limit: q.limit, used: q.used }))
      .catch(() => setQuota(null));
  }, [user?.plan]);

  const refreshAnalysis = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    setError(null);
    try {
      const res = await api.refreshAnalysis(symbol);
      setAnalysis(res.analysis);
      setQuota({ limit: res.limit, used: res.used });
    } catch (e) {
      // 402 (not on this plan) and 429 (quota gone) both carry a message that
      // says what to do; anything else is a genuine failure.
      setError(e instanceof ApiError ? e.message : 'Could not refresh the analysis.');
    } finally {
      setRefreshing(false);
    }
  }, [symbol, refreshing]);

  // --- replay ---------------------------------------------------------------
  const [replay, setReplay] = useState<ReplayState>(IDLE_REPLAY);


  /**
   * Analysis is withheld during replay, and the UI says why.
   *
   * It was computed over the COMPLETE series, so rendering it against a
   * partially revealed chart is lookahead bias — the annotation knows what
   * happens next. Replay exists to test a read without that knowledge, so
   * leaving it on would defeat the only thing the feature is for, and would
   * flatter the analysis while doing it.
   */
  const visibleAnalysis = replay.active ? null : analysis;

  // What the user was last looking at, per device. Not synced: a phone and a
  // desktop sitting on different symbols is normal, and forcing them together
  // would move one of them under the trader while they watched it.
  useEffect(() => { if (symbol) saveLastSymbol(symbol); }, [symbol]);

  const saveWatchlist = useCallback((next: string[]) => {
    // Optimistic: pinning is a list reorder, and waiting a round trip to see a
    // star fill in reads as broken. A failed save reverts.
    const before = watchlist;
    setWatchlist(next);
    void api.saveWatchlist(next).catch(() => setWatchlist(before));
  }, [watchlist]);

  // --- boot -----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [h, accts, insts, wl] = await Promise.all([
          api.health(), api.accounts(), api.instruments(),
          // A signed-out or demo caller has no watchlist, and failing to fetch
          // one must not take the chart down with it.
          api.watchlist().catch(() => ({ roots: [] as string[], max: 12 })),
        ]);
        if (cancelled) return;
        setHealth(h);
        setSymbols(h.symbols);
        setInstruments(insts.instruments);
        setAccounts(accts.accounts);
        setAccountId(accts.accounts[0]?.id ?? null);
        setWatchlist([...wl.roots]);
        setWatchlistMax(wl.max);

        // Resolved here rather than as a `useState` literal, because it needs
        // the feed AND the master AND the pins, and none of those exist until
        // now. `defaultSymbol` is the same function the phone calls.
        setSymbol((current) => current || defaultSymbol(
          buildCatalog(insts.instruments, new Set(h.symbols)),
          { lastViewed: loadLastSymbol(), pinned: wl.roots },
        ) || '');
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to reach the API.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // --- websocket ------------------------------------------------------------
  //
  // The socket lives in `MarketFeedProvider` and the subscription lives in
  // `useChartData`, which is the thing that wants the messages. What is left
  // here is the connection badge and the fact that a feed error is one of
  // several things that can raise this screen's error banner.
  const { connection: conn, error: feedError } = useMarketFeed();
  useEffect(() => { if (feedError) setError(feedError); }, [feedError]);

  // --- account data ---------------------------------------------------------
  const refreshAccount = useCallback(async (id: string) => {
    const [t, r, an] = await Promise.all([
      api.trades(id), api.rules(id), api.analytics(id),
    ]);
    setTrades(t.trades);
    setOpenTrades(t.openTrades);
    setRules(r);
    setAnalytics(an);

    // Score and scenarios are per-user rather than per-account, and both are
    // read-only summaries — a failure here must not take the analytics screen
    // down with it, so they settle independently.
    const [sc, sn] = await Promise.allSettled([api.score(), api.scenarios()]);
    if (sc.status === 'fulfilled') setScore(sc.value);
    if (sn.status === 'fulfilled') setScenarios(sn.value);
  }, []);

  useEffect(() => {
    if (!accountId) return;
    void refreshAccount(accountId).catch((e) =>
      setError(e instanceof Error ? e.message : 'Failed to load account.'));
    const id = setInterval(() => { void refreshAccount(accountId).catch(() => {}); }, 12_000);
    return () => clearInterval(id);
  }, [accountId, refreshAccount]);

  useEffect(() => {
    if (!accountId) return;
    void api.overlay(accountId, symbol).then(setOverlay).catch(() => setOverlay(null));
  }, [accountId, symbol]);

  const account = useMemo(
    () => accounts.find((a) => a.id === accountId) ?? null,
    [accounts, accountId],
  );

  const dayPnl = rules?.realizedDayPnl ?? null;

  /**
   * Decimals for every price in the header, from the instrument master.
   *
   * Was computed inline at the one place that needed it; there are two now, and
   * two copies of `?? 2` are two chances for one to drift.
   */
  const precision = instruments.find((i) => i.root === symbol)?.displayPrecision ?? 2;
  const change = netChange(daily, lastPrice);
  const changeTone = change === null || change.absolute === 0 ? ''
    : change.absolute > 0 ? 'up' : 'down';

  async function syncNow(): Promise<void> {
    if (!accountId || syncing) return;
    setSyncing(true);
    setError(null);
    try {
      await api.sync(accountId);
      await refreshAccount(accountId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  }

  // Layout mode, not screen width. The rail and the tab bar are different
  // components; see lib/viewport.ts for why this is a hook and not a media query.
  const isMobile = useIsMobile();

  const onChart = route === '/';
  const onJournal = route === '/journal';
  const onImport = route === '/import';
  /** Phone-only destinations. On a desktop the rail already lists all ten. */
  const onMore = route === '/more';
  const onToday = route === '/today';
  // `/trade/<id>`. Split rather than matched by prefix so `/trades` — should it
  // ever exist — does not silently render a detail screen for a missing id.
  const tradeDetailId = route.startsWith('/trade/') ? route.slice('/trade/'.length) : null;
  const onAnalytics = route === '/analytics';
  const onStrategies = route === '/strategies';
  const onDaily = route === '/daily';
  const onReports = route === '/reports';
  const onAgents = route === '/agents';
  const onBacktest = route === '/backtest';
  const onFundamentals = route === '/fundamentals';
  const onAlerts = route === '/alerts';
  const onTax = route === '/tax';
  const onSettings = route.startsWith('/settings');

  // The rail: pinned is remembered, peek is not. See `lib/rail.ts`.
  const [pinned, setPinned] = useState<boolean>(loadPinned);
  const [peek, setPeek] = useState(false);
  const railOpen = pinned || peek;
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPeek = useCallback(() => {
    if (peekTimer.current !== null) { clearTimeout(peekTimer.current); peekTimer.current = null; }
  }, []);
  // Clearing on unmount matters because the timer closes over `setPeek`; a
  // pointer that leaves as the route changes would otherwise fire into a
  // component that is gone.
  useEffect(() => clearPeek, [clearPeek]);

  const openPeek = useCallback(() => {
    clearPeek();
    peekTimer.current = setTimeout(() => setPeek(true), PEEK_DELAY_MS);
  }, [clearPeek]);
  const closePeek = useCallback(() => { clearPeek(); setPeek(false); }, [clearPeek]);

  const mod = useMemo(
    () => modifierKey(typeof navigator === 'undefined' ? '' : navigator.platform ?? ''),
    [],
  );

  const railItems = useMemo(() => ([
    ['/', 'Chart', 'chart'],
    // Directly under Chart: it is the other half of "what is happening",
    // and a trader reaches for it in the same breath.
    ['/fundamentals', 'Fundamentals', 'globe'],
    ['/journal', 'Journal', 'journal'],
    // Directly under Journal, because for a new user it is how the journal
    // starts — and docs/17 names an abandoned history as the top reason
    // somebody tries AEGIS and stays with what they had.
    ['/import', 'Import', 'receipt'],
    ['/analytics', 'Analytics', 'analytics'],
    ['/strategies', 'Strategies', 'target'],
    ['/reports', 'Reports', 'table'],
    ['/agents', 'Agents', 'ai'],
    ['/backtest', 'Backtest', 'replay'],
    ['/daily', 'Daily', 'calendar'],
    ['/alerts', 'Alerts', 'alerts'],
    ['/settings', 'Settings', 'settings'],
  ] as const), []);

  // ⌘1…⌘0 jump to the first ten sections — wired so the hint the rail prints
  // when it is open is not a promise the app breaks.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const i = shortcutIndex(e.key, {
        meta: e.metaKey, ctrl: e.ctrlKey, alt: e.altKey, shift: e.shiftKey,
      });
      if (i === null) return;
      const item = railItems[i];
      if (!item) return;
      e.preventDefault();
      closePeek();
      navigate(item[0]);
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [railItems, closePeek]);

  // Resolved per table, because the label depends on which symbols are on
  // screen: two tables of different contracts can honestly name different
  // things under the same preference. See `lib/zone.ts`.
  const openZone = useMemo(
    () => zoneResolver(tzPref, zoneInstruments, openTrades.map((t) => t.symbol)),
    [tzPref, zoneInstruments, openTrades],
  );
  const closedZone = useMemo(
    () => zoneResolver(tzPref, zoneInstruments, trades.map((t) => t.symbol)),
    [tzPref, zoneInstruments, trades],
  );

  /*
    Which side the trader is on in the charted symbol, if any.

    Undefined when nothing is open, and undefined when both sides are open at
    once — that is a real state (two accounts, opposite legs) and picking one
    of them to narrow the mirror by would be a guess dressed as a fact.
  */
  const openDirection = useMemo<'long' | 'short' | undefined>(() => {
    const sides = new Set(
      openTrades.filter((t) => t.symbol === symbol).map((t) => t.direction),
    );
    return sides.size === 1 ? ([...sides][0] as 'long' | 'short') : undefined;
  }, [openTrades, symbol]);


  /*
    The trader's layout, and the desktop windows it can spill into.

    Loaded once here rather than inside the grid: the switcher and the grid
    read the same state, and two hooks would be two fetches and two chances to
    disagree about which layout is active.
  */
  const workspace = useWorkspaces();

  /**
   * The chart the screen's data and the replay controls attach to.
   *
   * First preference is a chart that FOLLOWS the screen's symbol: it draws the
   * series the header, the analysis panel and the order-flow panels are already
   * reading, so there is no sense fetching it twice.
   *
   * Failing that, the first chart in the layout — and this fallback is the
   * whole of the fix for a workspace whose charts are all pinned. Without it
   * `primaryChartId` was null, no pane received the replay controls, and replay
   * simply could not be entered, with nothing on screen to say why.
   *
   * The consequence is deliberate and has to be visible: replay then runs on
   * THAT pane's instrument rather than the screen's, so the badge below names
   * it. A trader who pinned their only chart to YM and pressed replay means to
   * replay YM.
   */
  const primaryChart = workspace.active ? primaryChartPane(workspace.active) : null;
  const primaryChartId = primaryChart?.id ?? null;

  /**
   * The instrument the replay is actually stepping through.
   *
   * The screen's, unless the pane holding the controls is pinned to something
   * else. Read by the badge, and by the effect that ends a replay when its
   * series changes underneath it.
   */
  const replaySymbol = primaryChart?.config?.symbol ?? symbol;

  // Leaving the REPLAYED symbol or the timeframe ends the replay. Carrying a
  // cursor across a data change would point at a bar that no longer exists
  // there. Keyed on the replayed series rather than the screen's, so changing
  // the header symbol no longer cancels a replay running on a pinned chart.
  useEffect(() => { setReplay(IDLE_REPLAY); }, [replaySymbol, timeframe]);
  const [detachable, setDetachable] = useState(false);

  /*
    One crosshair bus for the whole screen, created once.

    Deliberately not React state: a crosshair moves on every pointer event, and
    routing that through a re-render would redraw every panel dozens of times a
    second. Charts subscribe to it directly and nothing here re-renders.
  */
  const crosshair = useRef(createCrosshairBus()).current;

  useEffect(() => {
    let live = true;
    void detachableKinds().then((k) => { if (live) setDetachable(k.length > 0); });
    return () => { live = false; };
  }, []);

  /*
    Restore the windows this layout says are torn off.

    On launch, and on every switch. A workspace that remembers a detached
    ladder and does not reopen it has a panel in neither place — not in the
    grid, because the layout says it is detached, and not in a window, because
    nothing opened one.

    Closes the panes that are NOT in this layout at the same time, so switching
    from a two-monitor layout to a single-screen one leaves no orphan window
    behind on the second monitor.
  */
  useEffect(() => {
    const ws = workspace.active;
    if (!ws || !detachable) return;
    void (async () => {
      const open = await openPaneKinds();
      for (const paneId of ws.detached) {
        // The window opens on the pane's OWN symbol, not the main window's. A
        // CL ladder torn off must not come back as an ES one.
        if (!open.includes(paneId)) {
          await openPane(paneId, paneSymbolOf(ws, paneId) ?? symbol ?? null);
        }
      }
      for (const paneId of open) {
        if (!ws.detached.includes(paneId)) await closePane(paneId);
      }
    })();
    // Deliberately NOT keyed on `symbol`: a pane is told its symbol when it
    // opens and does not follow the main window afterwards, so re-running this
    // on every symbol change would reopen every window on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace.activeId, workspace.active?.detached.join(','), detachable]);

  const openDetached = useCallback((paneId: string) => {
    const ws = workspace.active;
    void openPane(paneId, (ws && paneSymbolOf(ws, paneId)) ?? symbol ?? null);
  }, [symbol, workspace]);

  /** Bring one home: close the window, then put it back in the grid. */
  const bringHome = useCallback((paneId: string) => {
    void closePane(paneId);
    workspace.edit((ws) => attachPane(ws, paneId));
  }, [workspace]);

  /*
    The panels, as nodes.

    `WorkspaceGrid` owns where things sit and calls this for what goes in them.
    Keeping the data here means adding a panel does not mean threading its
    props through the layout, and it is why the grid can be a pure function of
    a workspace plus a renderer.
  */
  /**
   * The replay controls, and which pane they belong to.
   *
   * Replay is a mode over the session rather than a property of a canvas: one
   * cursor, one fine series, one set of controls. It attaches to the pane that
   * follows the screen's symbol — the first chart with no symbol of its own.
   * A chart deliberately pointed at CL is not what the screen's replay is
   * replaying, so it does not get the controls.
   */
  const replayControls = (ctx: ReplayContext): JSX.Element => (
    <ReplayControls
      state={replay}
      total={ctx.total}
      barTime={ctx.barTime}
      onChange={setReplay}
      onExit={() => setReplay(IDLE_REPLAY)}
      locked={!(user?.plan === 'pro')}
      onUpgrade={() => navigate('/pricing')}
      maxSteps={ctx.maxSteps}
      stepLabel={ctx.stepLabel}
      fineNote={ctx.fineNote}
    />
  );


  const analysisPanel = (
    <>

      {/* ---- the annotation legend ----
          Phone only, and it is not decoration: on a phone the price
          lines are drawn WITHOUT their titles, because Lightweight
          Charts floats a title over the plot at the line's height and
          seven of them at 390pt overlap each other and the price scale.

          The lines are still there. This is where their labels went —
          in price order, which the floating version never was, and
          legible, which it also never was. */}
      {isMobile && showAnnotations && !replay.active
        && (analysis?.annotations.length ?? 0) > 0 && (
        <ul className="ann-legend">
          {analysis!.annotations
            .filter((a) => a.kind === 'level' && a.price !== undefined)
            .slice()
            .sort((a, b) => (b.price ?? 0) - (a.price ?? 0))
            .map((a) => {
              const isSession = a.evidence.some((e) => e.includes('session'));
              return (
                <li key={`${a.label}-${a.price}`} className="ann-row">
                  <span className={`ann-swatch ${isSession ? 'session' : 'ai'}`} aria-hidden="true" />
                  <span className="ann-label">{a.label}</span>
                  <span className="ann-price mono">
                    {fmtNum(a.price!, instruments.find((i) => i.root === symbol)?.displayPrecision ?? 2)}
                  </span>
                </li>
              );
            })}
        </ul>
      )}
      <div className="chart-side-controls">
        <label className="toggle">
          <input
            type="checkbox"
            checked={showAnnotations}
            onChange={(e) => setShowAnnotations(e.target.checked)}
          />
          Show AI annotations
        </label>
        {/* Off the broadcast cadence, and quota-bounded. ADR-0006's
            whole argument is that AI cost tracks the symbol universe
            rather than the user count, so this is the one place a user
            action can spend, and the count is always in view. */}
        {quota && quota.limit > 0 && !replay.active && (
          <button
            className="btn btn-quiet compact refresh-analysis"
            onClick={() => void refreshAnalysis()}
            disabled={refreshing || quota.used >= quota.limit}
            title={quota.used >= quota.limit
              ? 'No on-demand analyses left this month'
              : 'Recompute this symbol now'}
          >
            {refreshing ? 'Analysing…' : `Refresh · ${quota.limit - quota.used} left`}
          </button>
        )}
      </div>
      {replay.active ? (
        <EmptyState
          icon="ai"
          title="Analysis hidden during replay"
          note={
            <p>
              Replay is for testing a read without that knowledge. Leaving them
              on would defeat the point, and would make the analysis look better
              than it is.
            </p>
          }
        >
          <p>
            The analysis was computed over the whole series, so showing it here
            would mean the annotations already know what happens next.
          </p>
        </EmptyState>
      ) : (
        <AnalysisPanel analysis={analysis} overlay={overlay} disclaimer={disclaimer}
                       direction={openDirection} />
      )}
    </>
  );

  /**
   * One panel's contents.
   *
   * A plain function rather than a memo: every branch closes over state that
   * changes on most renders, so a dependency list would either be wrong or be
   * every value in this component.
   */
  const renderPanel = (pane: Pane): ReactNode => {
    // A panel's own symbol wins over the application's. That is the whole
    // point of per-instance config: a ladder deliberately pointed at CL keeps
    // CL when the main window moves, while one that never chose follows.
    const at = pane.config?.symbol ?? symbol;
    const px = instruments.find((i) => i.root === at)?.displayPrecision ?? 2;

    switch (pane.kind) {
      case 'chart': {
        /*
          Every chart in the layout, each with its own series.

          The primary one — the first chart with no symbol of its own — draws
          the screen's data, because the header, the analysis panel and the
          order-flow panels all read that same series and there is no sense
          fetching it twice. Every other chart fetches for itself.

          Both render the same view. The difference is only where the data
          comes from.
        */
        const common = {
          pane, instruments, style: chartStyle, trades,
          showAnnotations, readOnly: isMobile, crosshair,
          crosshairTargets: workspace.active
            ? crosshairTargets(workspace.active, pane.id, symbol)
            : [],
        };
        return (
          <div className="chart-pane" data-pane={pane.id}>
            {pane.id === primaryChartId ? (
              <ChartPane
                {...common}
                symbol={symbol}
                timeframe={timeframe}
                data={chartData}
                drawings={drawingState}
                replay={{
                  state: replay,
                  hideAnalysis: replay.active,
                  controls: replayControls,
                }}
              />
            ) : (
              <OwnChartPane
                {...common}
                fallbackSymbol={symbol}
                fallbackTimeframe={timeframe}
                onError={setError}
              />
            )}
          </div>
        );
      }
      case 'analysis': return analysisPanel;
      case 'rules': return <RulePanel state={rules} />;
      case 'positions':
        return openTrades.length > 0
          ? (
            <TradeTable trades={openTrades} onSelect={setSelectedTrade}
                        selectedId={selectedTrade?.id ?? null} zone={openZone} />
          )
          : <p className="panel-empty">Nothing open.</p>;
      case 'book':
        return <OrderBook symbol={at} precision={px} live={!replay.active} />;
      case 'tape':
        return <Tape symbol={at} precision={px} live={!replay.active} />;
      case 'footprint':
        return <Footprint symbol={at} live={!replay.active} />;
      case 'profile':
        /*
          Not hidden during replay, unlike the book and the tape.

          Those two describe what is resting or printing RIGHT NOW, which is
          exactly the knowledge replay withholds. A profile is a completed
          shape over a past window — the same thing a trader would have been
          looking at, and reading it against a replayed chart is the point
          rather than a leak.
        */
        return <ProfilePanel symbol={at} timeframe={timeframe} onError={setError} />;
      case 'studies':
        /*
          Not symbol-scoped and not hidden during replay: the panel manages the
          list, and the studies themselves are drawn on the charts against
          whatever those charts are showing — replayed bars included.
        */
        return <StudiesPanel onError={setError} />;
      case 'news':
        // Hidden during replay for the same reason the book is: news from this
        // morning beside a chart of three weeks ago is the confusion the
        // replay badge exists to prevent.
        return replay.active
          ? <p className="panel-empty">Hidden during replay.</p>
          : <SymbolNews symbol={at} />;
      case 'journal':
        return trades.length > 0
          ? (
            <TradeTable trades={[...trades].reverse()} onSelect={setSelectedTrade}
                        selectedId={selectedTrade?.id ?? null} zone={closedZone} />
          )
          : <p className="panel-empty">No closed trades yet.</p>;
      case 'precedent':
        return <PrecedentPanel symbol={at} direction={openDirection} />;
      case 'portfolio': return <PortfolioRiskPanel />;
      case 'notes': return <NotesPanel />;
      default: return null;
    }
  };

  return (
    <div className={`app ${isMobile ? 'is-mobile' : ''}`}>
      {/* ---- tool rail ----
          Collapsed is the resting state, because the chart is the product and
          chrome does not get to keep width in order to explain itself. Two ways
          to open it: hover (or tab in) and the labels PEEK out over the content
          without the layout moving, or pin it and the column widens for good.

          Labels stay in the accessibility tree in both states — collapsing
          hides them from the eye only, so a screen reader hears "Journal"
          whether or not the rail is open. */}
      {!isMobile && (
      <nav
        className={`rail ${pinned ? 'pinned' : ''}`}
        data-open={railOpen ? 'true' : 'false'}
        aria-label="Sections"
        onMouseEnter={pinned ? undefined : openPeek}
        onMouseLeave={pinned ? undefined : closePeek}
        onFocus={pinned ? undefined : () => { clearPeek(); setPeek(true); }}
        onBlur={pinned ? undefined : (e) => {
          // Only close when focus actually leaves the rail — tabbing between
          // its own buttons fires blur too.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) closePeek();
        }}
      >
        <div className="rail-body">
          {railItems.map(([to, label, icon], i) => {
            // The trade detail lives under the journal as far as the rail is
            // concerned: it is where you came from and where Back returns you.
            const active = to === '/journal'
              ? route === '/journal' || tradeDetailId !== null
              : to === '/' ? route === '/' : route.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                className={`rail-btn compact ${active ? 'on' : ''}`}
                aria-current={active ? 'page' : undefined}
                // Collapsed, the tooltip is the only name a mouse user gets.
                title={railOpen ? undefined : label}
                onNavigate={closePeek}
              >
                <span className="rail-ico"><Icon name={icon} /></span>
                <span className="rail-label">{label}</span>
                {i < 10 && (
                  <span className="rail-key" aria-hidden="true">{mod}{(i + 1) % 10}</span>
                )}
              </Link>
            );
          })}

          <div className="rail-sep" aria-hidden="true" />

          <button
            type="button"
            className="rail-btn compact rail-toggle"
            onClick={() => {
              const next = !pinned;
              setPinned(next);
              savePinned(next);
              if (next) setPeek(false);
            }}
            aria-expanded={pinned}
            aria-label={pinned ? 'Collapse the section rail' : 'Keep the section rail open'}
            title={pinned ? 'Collapse the rail' : 'Keep the rail open'}
          >
            <span className="rail-ico"><Icon name="chevronRight" /></span>
            <span className="rail-label">{pinned ? 'Collapse' : 'Keep open'}</span>
          </button>
        </div>
      </nav>
      )}

      <div className="main">
        {/*
          The nudge for somebody who skipped setup.

          Above the header rather than inside Today, because `/today` is a
          mobile-web route and a desktop user would never have seen it. One
          strip, every surface, dismissible, and it renders nothing at all for
          a user who finished or never skipped.
        */}
        <FinishSetupNudge />

        {/* ---- header ---- */}
        <header className="header">
          <Link to="/" className="brand">AEGIS</Link>

          <SymbolPicker
            value={symbol}
            onChange={setSymbol}
            instruments={instruments}
            available={symbols}
            pinned={watchlist}
            pinnedMax={watchlistMax}
            onPinnedChange={saveWatchlist}
          />

          <div className="tf-group" role="group" aria-label="Timeframe">
            {TIMEFRAMES.map((tf) => (
              <button
                key={tf}
                className={`tf compact ${timeframe === tf ? 'on' : ''}`}
                onClick={() => setTimeframe(tf)}
                aria-pressed={timeframe === tf}
              >{tf}</button>
            ))}
          </div>

          {/* Suppressed during replay. A live price beside a chart from three
              weeks ago is the exact confusion the replay badge exists to
              prevent, and the header is where the eye goes first. */}
          {/* Decimals come from the contract, not from a constant. YM ticks in
              whole points and NG in thousandths; two decimals is right for
              neither, and this is the largest number on the screen. */}
          {lastPrice !== null && !replay.active && (
            <span className="last-price mono">{fmtNum(lastPrice, precision)}</span>
          )}

          {/*
            How far it has moved this session, beside the number it qualifies.

            `.last-change` joins `.last-price` on the still-surfaces list: it is
            a figure, and a figure that eases between two values displays every
            value in between. Suppressed during replay for the same reason the
            price is — a live net change beside a chart from three weeks ago is
            the confusion the replay badge exists to prevent.
          */}
          {change && lastPrice !== null && !replay.active && (
            <span className={`last-change mono ${changeTone}`}>
              {formatNetChange(change, precision)}
            </span>
          )}

          {/*
            What the prices ARE, before anything about how stale they are.

            A generated price is not a delayed price, and until Phase 51 the
            header had no way to say so — every price in the product was
            generated and the only badge described the entitlement tier. Shown
            FIRST, and the staleness badge is suppressed underneath it: "10-MIN
            DELAYED" on top of a simulation describes the licence, not the data,
            and stacking both invites the reader to believe the reassuring one.
          */}
          {dataSource === 'mock' ? (
            <span
              className="badge badge-warn"
              title="These prices are generated by AEGIS, not received from an exchange or a broker. Nothing here is a real market."
            >
              SIMULATED
            </span>
          ) : delayedByMs > 0 && (
            <span className="badge badge-warn" title={dataReason}>
              {/* Shared with the phone, which had its own arithmetic and
                  rendered the free tier as `Delayed 480 min`. */}
              {delayLabel(delayedByMs).toUpperCase()}
            </span>
          )}

          {marketStatus && !marketStatus.isOpen && (
            <span className="badge badge-muted" title={marketStatus.reason}>
              <StatusDot state={marketStatus.state} /> {marketStatus.state.replace(/_/g, ' ').toUpperCase()}
            </span>
          )}

          <div className="header-right">
            {dayPnl !== null && (
              <span className="header-pnl">DAY <Pnl value={dayPnl} /></span>
            )}
            {/*
              CONNECTED, not LIVE.

              This is the WebSocket's state and nothing else. It sat next to a
              staleness badge reading "8H DELAYED" and the pair read as a
              contradiction — one of them appearing to describe the data and
              being wrong about it. The socket being open says a socket is open.
            */}
            <span className={`conn conn-${conn}`} title={`Feed ${conn}`}>
              <StatusDot state={conn === 'open' ? 'open' : 'closed'} />
              {conn === 'open' ? 'CONNECTED' : conn.toUpperCase()}
            </span>
            {user && <CreditMeter load={api.credits} />}
            {user && user.plan === 'free' && (
              <Link to="/pricing" className="btn btn-primary compact">Upgrade</Link>
            )}
          </div>
        </header>

        <UpdateNotice />

        {status === 'demo' && (
          <div className="notice-bar" role="status">
            Mock mode — this is the seeded demo identity, and nothing you do here is
            kept. <Link to="/signup">Create an account</Link> to keep a journal.
          </div>
        )}

        {/*
            Replay is running on a chart pinned to something other than the
            symbol the rest of the screen is about.

            Named rather than left to be noticed. The header price, the analysis
            panel and the order-flow panels are all describing `symbol`, and the
            replayed chart is not — which is a reasonable thing to want and an
            unreasonable thing to have to work out from the axis.
        */}
        {replay.active && replaySymbol !== symbol && (
          <div className="notice-bar" role="status">
            Replaying <strong>{replaySymbol}</strong> — the chart holding the replay
            controls is pinned to it. Everything else on screen is about {symbol}.
          </div>
        )}

        {notice && (
          <div className="notice-bar" role="status">
            {notice}
            <button className="compact error-dismiss" onClick={dismissNotice}>dismiss</button>
          </div>
        )}

        {error && (
          <div className="error-bar" role="alert">
            {error}
            <button className="compact error-dismiss" onClick={() => setError(null)}>dismiss</button>
          </div>
        )}

        {/* ---- content ---- */}
        {/*
          The chart screen, arranged by the trader.

          On a wide screen this is a workspace: columns the trader fills,
          drags and resizes, saved server-side so the arrangement made on a
          desktop is there on a laptop. On a phone it is the fixed stack it
          has always been — a multi-column workspace on a 390pt screen is a
          column of one panel each, which is not an arrangement, and the
          controls to make one would take more room than the panels.
        */}
        {onChart && !isMobile && workspace.active && (
          <div className="ws-screen">
            <WorkspaceBar
              all={workspace.all}
              active={workspace.active}
              activeId={workspace.activeId}
              panels={workspace.panels}
              limit={workspace.limit}
              refusal={workspace.refusal}
              dismissRefusal={workspace.dismissRefusal}
              onSwitch={workspace.switchTo}
              onCreate={(name, from) => void workspace.create(name, from)}
              onRemove={(id) => void workspace.remove(id)}
              onRename={workspace.rename}
              onEdit={workspace.edit}
              onAttach={bringHome}
            />
            <WorkspaceGrid
              workspace={workspace.active}
              onEdit={workspace.edit}
              onDetach={openDetached}
              canDetach={detachable}
              render={renderPanel}
              symbols={instruments.map((i) => i.root)}
              defaultSymbol={symbol}
            />
          </div>
        )}

        {onChart && (isMobile || !workspace.active) && (
          <div className="chart-layout">
            {/* No workspace to take a pane from — a phone has no layout, and
                neither does a session whose workspaces have not loaded. The
                chart is the same component either way; it is handed a pane
                that exists only for the duration of this render. */}
            <ChartPane
              pane={{ id: 'chart', kind: 'chart', weight: 1 }}
              symbol={symbol}
              timeframe={timeframe}
              data={chartData}
              drawings={drawingState}
              instruments={instruments}
              style={chartStyle}
              trades={trades}
              showAnnotations={showAnnotations}
              readOnly={isMobile}
              replay={{
                state: replay,
                hideAnalysis: replay.active,
                controls: replayControls,
              }}
            />
            <div className="chart-side">
              {analysisPanel}
              {!replay.active && <SymbolNews symbol={symbol} />}
              <OrderBook symbol={symbol} precision={precision} live={!replay.active} />
              <Footprint symbol={symbol} live={!replay.active} />
            </div>
          </div>
        )}

        {onJournal && (
          <div className="journal-layout">
            <div className="journal-main">
              <div className="section-head">
                <h2>Journal</h2>
                <div className="account-picker">
                  {accounts.map((a) => (
                    <button
                      key={a.id}
                      className={`chip-btn compact ${accountId === a.id ? 'on' : ''}`}
                      onClick={() => setAccountId(a.id)}
                    >
                      {a.nickname}
                    </button>
                  ))}
                  {accounts.length > 0 && (
                    <button
                      className="btn btn-quiet compact"
                      onClick={() => void syncNow()}
                      disabled={!accountId || syncing}
                      title="Re-import fills from the broker"
                    >
                      {syncing ? 'Syncing…' : 'Sync'}
                    </button>
                  )}
                </div>
              </div>
              {/* A new account has no broker attached, so the journal is empty
                  and stays empty until one is. "No trades recorded yet" is true
                  and useless; this says what is actually missing. */}
              {accounts.length === 0 && <NoBrokerYet health={health} />}
              {openTrades.length > 0 && (
                <>
                  <h3 className="panel-h">OPEN</h3>
                  <TradeTable trades={openTrades} onSelect={setSelectedTrade}
                              selectedId={selectedTrade?.id ?? null}
                              zone={openZone} />
                </>
              )}
              {accounts.length > 0 && (
                <>
                  <h3 className="panel-h">CLOSED · {trades.length}</h3>
                  <TradeTable trades={[...trades].reverse()} onSelect={setSelectedTrade}
                              selectedId={selectedTrade?.id ?? null}
                              zone={closedZone} />
                </>
              )}
            </div>
            {selectedTrade ? (
              <JournalEditor trade={selectedTrade} onClose={() => setSelectedTrade(null)} />
            ) : (
              <aside className="panel">
                <RulePanel state={rules} />
              </aside>
            )}
          </div>
        )}

        {onAnalytics && (
          <div className="scroll-area">
            <div className="section-head">
              <h2>Analytics</h2>
              <div className="account-picker">
                {account && (
                  <span className="dim">
                    {account.nickname} · started {fmtUsd(account.startingBalance)}
                  </span>
                )}
                <Link to="/tax" className="btn btn-quiet compact">Tax export</Link>
              </div>
            </div>
            {accounts.length === 0
              ? <NoBrokerYet health={health} />
              : (
                <>
                  <AnalyticsPanel a={analytics} />
                  <ScorePanel score={score} />
                  <ScenarioPanel s={scenarios} />
                </>
              )}
          </div>
        )}

        {tradeDetailId && <TradeDetailScreen tradeId={decodeURIComponent(tradeDetailId)} />}

        {onStrategies && <StrategiesScreen />}

        {onReports && <ReportsScreen />}
        {onImport && <ImportScreen accounts={accounts} />}

        {onAgents && <AgentsScreen />}

        {onBacktest && <BacktestScreen />}

        {onDaily && <DailyScreen />}

        {onFundamentals && <FundamentalsScreen />}

        {onAlerts && <AlertsScreen symbols={symbols} />}

        {route === '/tax' && (
          <TaxScreen accounts={accounts} accountId={accountId} onAccount={setAccountId} />
        )}

        {/* ---- Today ----
            Where someone away from their desk starts: what the day has done,
            what is still open, and how close the rules are.

            Composed from panels the desktop already has rather than built new.
            Every number here is one the shell has already fetched — a second
            screen with its own requests would show a different day's P&L to the
            one in the header for as long as the two were out of step.

            Not on the rail. On a desktop the header carries the day's P&L and
            the journal carries the rest, so a Today screen there would be a
            third place to read the same figures. */}
        {onToday && (
          <div className="scroll-area today">
            <section className="today-pnl">
              <span className="panel-h">TODAY</span>
              <span className="today-figure"><Pnl value={dayPnl} /></span>
              {/* Which account this is comes from RulePanel below, which names
                  it alongside the limits it is measuring against. Repeating it
                  here would be the same label twice on one screen. */}
            </section>

            {openTrades.length > 0 ? (
              <>
                <h3 className="panel-h">OPEN · {openTrades.length}</h3>
                <TradeTable trades={openTrades} onSelect={setSelectedTrade}
                            selectedId={selectedTrade?.id ?? null}
                            zone={openZone} />
              </>
            ) : (
              <p className="panel-body">Nothing open.</p>
            )}

            <RulePanel state={rules} />

            <h3 className="panel-h">LATEST READ · {symbol}</h3>
            <AnalysisPanel analysis={analysis} overlay={overlay} disclaimer={disclaimer}
                               direction={openDirection} />
          </div>
        )}

        {/* Phone-only. On a desktop the rail lists all ten destinations, so a
            screen whose whole job is to list seven of them would be a second
            navigation competing with the first. */}
        {onMore && <MoreScreen />}

        {onSettings && route === '/settings' && (
          <SettingsScreen health={health} dataReason={dataReason} />
        )}
        {route === '/settings/devices' && <DevicesScreen />}
        {route === '/settings/security' && <SecurityScreen />}
        {/* Admin only, and each screen says so rather than the router: the API
            answers 404 to anyone else, so there is nothing to hide by refusing
            to render them. */}
        {route === '/settings/errors' && <ErrorsScreen />}
        {route === '/settings/news-status' && <NewsStatusScreen />}
        {route === '/settings/ai-status' && <AiStatusScreen />}
        {route === '/settings/ai-audit' && <AiAuditScreen />}
        {route === '/settings/entitlements' && <EntitlementsScreen />}
        {route === '/settings/broker' && <BrokerScreen />}
        {route === '/settings/trading' && <PreferencesScreen />}

        {!onChart && !onJournal && !onAnalytics && !onStrategies && !onReports
          && !onAgents && !onBacktest && !onDaily && !onAlerts && !onTax && !onSettings
          && !onMore && !onToday && !onFundamentals && !onImport && tradeDetailId === null && (
          <div className="scroll-area">
            <h2>Not found</h2>
            <p className="panel-body">
              There is nothing at <code className="mono">{route}</code>.
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/')}>
              Back to the chart
            </button>
          </div>
        )}
      </div>

      {isMobile && <MobileTabBar route={route} />}
    </div>
  );
}
